/**
 * `RowTypeSpec` — per-row-type metadata registry, keyed by wire
 * type string (`"calendar"`, `"weather"`, `"formula"`, …).
 *
 * Sits alongside `DESCRIPTOR_BUILDERS` in `raw-sheet.ts`.
 * Descriptor builders produce the row's coarse shape
 * (`SourceDescriptor` / `LayoutDescriptor` / etc.); `RowTypeSpec`
 * entries carry the per-wire-type **metadata** that lona-js
 * historically kept on `row-type.ts`'s per-descriptor objects —
 * default attributes, height bounds, child-spec seeds,
 * subsumption config, active-layout resolution.
 *
 * Phase 1a scaffolds the type + registry + `reconcileAttributes`
 * helper; Phase 3d populates the registry from lona-js's per-
 * variant tables. Until 3d lands, the registry is empty and
 * `resolveRowTypeSpec` returns `null` for every wire type — the
 * hydrator + `Sheet.addRow` path fall through to the "no
 * defaults" case (rawRow's attributes pass through unchanged).
 *
 * Alias templates: `sheets/.docs/content/rows-aliases.md`. Behaviour
 * keyed by wire type or role lives in central registries like this
 * one, not in per-class statics.
 */

import type { Option } from "../core/option.ts";
import { StaticRegistry } from "../sheet/static-registry.ts";
import { Rational } from "@tento-core/rational";

import type { DataPresetSpec } from "./data-preset-spec.ts";
import type { Graph } from "../stores/graph-types.ts";
import { Json, type JsonObject } from "../core/json.ts";
import type { RowNetworkKey } from "../ids/row-network-key.ts";
import type { RowLayoutShape } from "../render/layout-types.ts";
import { isRendererWireType } from "../render/renderer-kind.ts";
import type { RowMetadata } from "../stores/row-metadata-store.ts";
import { deepFrozen } from "../core/frozen-collections.ts";

/** The row metadata a row type spec defaults (`RowMetadata.meta`, never `null`). */
export type RowMeta = NonNullable<RowMetadata["meta"]>;

/**
 * Static spec for a row type — immutable, shared across every
 * instance of the type. `RowTypeSpec` fields are optional
 * because most row types use only a subset; a type with no
 * special behaviour has no entry (or an empty one).
 */
export interface RowTypeSpec {
  /**
   * Produce the default attribute bag for a new row of this type.
   * Called at hydrate time (seeding) and at `Sheet.addRow` (new
   * rows). Receives the wire row so defaults can read raw fields.
   * JSON only: the type's child seeds are `childSpecs`, not an
   * attribute.
   */
  readonly defaultAttributes?: (row: RawRowInput) => JsonObject;

  /**
   * Produce default server/runtime metadata for this row type.
   * Instance metadata from the wire wins over these defaults.
   *
   * Use this for generated cell encoding declarations such as
   * `encoding: "task.v1"` so rows-side hydration can decode wire
   * payloads before facades/render providers read them.
   */
  readonly defaultMetadata?: (
    row: RawRowInput,
  ) => RowMeta;

  /**
   * Declarative child seeds expanded at hydrate time. Each
   * `ChildSpec` becomes a child row under the parent. Resolved
   * through `resolveChildren` which honors the `attributes
   * .children` visibility map set by instances. Receives the wire
   * row so the seeds can depend on it (a data row's chart preset
   * follows its lookup key). A type that declares this owns its
   * children: an instance's wire `attributes.childSpecs` is ignored
   * (see `RowTypeSpecOps.childSpecsFor`).
   */
  readonly childSpecs?: (row: RawRowInput) => readonly ChildSpec[];

  /**
   * Choose the active layout key for this row based on the
   * sheet's timescale. Returns the key that descendants use
   * for renderer-id prefix subsumption. Unset when the
   * type doesn't provide a slot.
   */
  readonly activeLayout?: (timescale: string) => Option<string>;

  /**
   * Whether children of this row render (default: `true`).
   * Called with the instance's reconciled attributes so the
   * answer can depend on config (e.g. a collapsed block hides
   * its children).
   */
  readonly rendersChildren?: (
    attributes: Option<JsonObject>,
  ) => boolean;

  /** Height clamp for this type. `Row.setHeight` enforces. */
  readonly heightBounds?: { readonly min: number; readonly max: number };

  /**
   * Logical layout shape — drives `RowRenderMode.compute`'s
   * span / stack / span-stacked dispatch. Pixel heights stay out
   * of this entry (they live in DOM-side height bundles); the
   * shape is the platform-agnostic part that CLI / tests / terminal
   * consumers can read directly.
   *
   * Returns the static shape for the row type; attribute / timescale
   * dependence is allowed when needed (calendar's `disableMonthlyView`
   * attribute can flip `spannable`, for example).
   *
   * Absent → row uses the neutral default (no stacking, no spanning).
   */
  readonly layoutShape?: (
    attributes: Option<JsonObject>,
    sheetTimescale: string,
  ) => RowLayoutShape;
}

/**
 * Minimal raw-row shape `defaultAttributes` callbacks can read.
 * Mirrors `RawRow` without pulling the whole type — keeps this
 * module DOM-free and lightweight.
 */
export interface RawRowInput {
  readonly id: string;
  readonly type: string;
  readonly label?: string;
  readonly lookupKey?: string;
  readonly attributes?: JsonObject;
}

/** The default heights in pixels of a data row's chart children (`canvas`, `series`). */
export const DATA_GRAPH_ROW_HEIGHTS = {
  canvas: 220,
  series: 160,
} as const;
deepFrozen(DATA_GRAPH_ROW_HEIGHTS);

/** ChildSpec — declarative child-row seed (pre-expansion). */
export interface ChildSpec {
  readonly preset: string;
  readonly type: string;
  readonly label?: string;
  readonly lookupKey?: string;
  readonly formula?: string;
  readonly heightPx?: number;
  readonly position: Rational;
  readonly storage: "parent" | "own";
  readonly placement: "child" | "root";
  /**
   * The child row's attribute bag: JSON, plus the `PARENT_ROW_ID`
   * sentinel the expansion replaces with the parent's row id.
   */
  readonly attributes?: ChildSpecAttributes;
  /**
   * Closures the child row carries beside its attributes (a data
   * preset's x-domain mapping and extractors — see `Graph.Hooks`).
   * The expansion hands them to the row's `graphHooks`; they never
   * enter the attribute bag.
   */
  readonly graph?: Graph.Hooks;
  /**
   * Server-managed metadata (mirrors `RawRow.meta`). Use for
   * client-known invariants like `encoding: "event.v1"` on
   * canonical provider-backed source rows the alias factory
   * synthesizes locally — the rows would otherwise carry no
   * encoding because they're never fetched as rows themselves.
   */
  readonly meta?: JsonObject;
  readonly children?: readonly ChildSpec[];
  /**
   * Hydration-reconcile opt-in. An already-hydrated alias (authored or
   * forked — its children are real, server-backed rows, so the template
   * no longer drives synthesis) normally ignores factory child specs.
   * A spec marked `reconcile: true` is the exception: it is additively
   * synthesized whenever the materialized subtree is missing it, so a
   * child ADDED to the factory after instances were materialized still
   * appears (e.g. the sticker subcolumn overlaying existing calendars).
   *
   * Reconcile is opt-in per spec because a blind "add every missing
   * factory child" pass would corrupt specialized authored instances
   * (e.g. the scheduling-link / onboarding calendars) that intentionally
   * omit factory children. Use it only for additive, idempotent overlays
   * that belong on every instance of the template regardless of how it
   * was authored. See `alias-templates.ts:reconcileChildSpecsAdditive`.
   */
  readonly reconcile?: boolean;
}

/**
 * Sentinel for `ChildSpec.attributes` — the hydrate-time
 * expansion substitutes this placeholder with the parent row's
 * `r_<hex>` id string. Used by specs whose children need a
 * reverse reference to their parent (e.g. calendar-list's
 * `linkedCalendarRowId`).
 *
 * Ported verbatim from `tento/tento-lona-js/src/row-type.ts:33` — pointer
 * equality matters, so both call sites compare against this
 * exact symbol.
 */
export const PARENT_ROW_ID: unique symbol = Symbol("parentRowId");

/**
 * A `ChildSpec`'s attribute bag: JSON values, or the `PARENT_ROW_ID`
 * sentinel the expansion substitutes with the parent's `r_<hex>` id.
 */
export type ChildSpecAttributes = {
  readonly [key: string]: Json | typeof PARENT_ROW_ID;
};

/**
 * Replace every top-level `PARENT_ROW_ID` in `attributes` with
 * `parentRowId`, yielding the JSON bag the child row carries.
 */
function resolveParentRowId(
  attributes: ChildSpecAttributes,
  parentRowId: RowNetworkKey,
): JsonObject {
  const out: { [key: string]: Json } = {};
  for (const [key, value] of Object.entries(attributes)) {
    out[key] = value === PARENT_ROW_ID ? parentRowId : value;
  }
  return out;
}

/**
 * Filter `specs` down to the visible subset. A spec is visible
 * unless `attributes.children[spec.preset].visible === false`.
 * The `children` map is the reconciled-attributes visibility
 * bag — instance wins per-preset (see `reconcileAttributes`
 * deep merge).
 *
 * Ported verbatim from `tento/tento-lona-js/src/row-type.ts:51`.
 */
function resolveChildren(
  specs: readonly ChildSpec[],
  attributes: Option<JsonObject>,
): ChildSpec[] {
  const childrenConfig = Json.child(attributes, "children");
  return specs.filter((spec) =>
    Json.boolean(Json.child(childrenConfig, spec.preset), "visible") ?? true
  );
}

/**
 * The child seeds of a row: its type's declared `childSpecs` when the
 * type declares them, else the JSON seeds the instance carries in its
 * wire `attributes.childSpecs` (the materialized form an app persists
 * for a hydrated alias — see `childSpecsFromAttributes`).
 */
function childSpecsFor(
  wireType: string,
  row: RawRowInput,
  registries: RowTypeSpecOps.Registries,
): readonly ChildSpec[] {
  const declared = registries.rowTypes.get(wireType)?.childSpecs;
  if (declared !== undefined) return declared(row);
  return childSpecsFromAttributes(row.attributes ?? null);
}

/**
 * Decode the JSON child seeds in `attributes.childSpecs`. Each entry
 * needs a string `preset` and `type` and a `placement` of `"child"` or
 * `"root"` (an entry without one was never expanded); `storage`
 * defaults to `"own"`. A `position` is `{ n, d }` or a sibling index;
 * absent, the entry's index. Malformed entries are dropped.
 */
function childSpecsFromAttributes(
  attributes: Option<JsonObject>,
): readonly ChildSpec[] {
  const list = Json.array(attributes, "childSpecs");
  return list == null ? [] : decodeChildSpecList(list);
}

function decodeChildSpecList(list: readonly Json[]): ChildSpec[] {
  const out: ChildSpec[] = [];
  list.forEach((item, index) => {
    const spec = decodeChildSpec(Json.object(item), index);
    if (spec != null) out.push(spec);
  });
  return out;
}

function decodeChildSpec(
  obj: Option<JsonObject>,
  index: number,
): Option<ChildSpec> {
  const preset = Json.string(obj, "preset");
  const type = Json.string(obj, "type");
  const placementAttr = Json.string(obj, "placement");
  if (obj == null || preset == null || type == null) return null;
  if (placementAttr !== "child" && placementAttr !== "root") return null;
  const label = Json.string(obj, "label");
  const lookupKey = Json.string(obj, "lookupKey");
  const formula = Json.string(obj, "formula");
  const heightPx = Json.number(obj, "heightPx");
  const attributes = Json.child(obj, "attributes");
  const meta = Json.child(obj, "meta");
  const children = Json.array(obj, "children");
  const reconcile = Json.boolean(obj, "reconcile");
  return {
    preset,
    type,
    position: decodePosition(obj["position"] ?? null, index),
    storage: Json.string(obj, "storage") === "parent" ? "parent" : "own",
    placement: placementAttr,
    ...(label != null ? { label } : {}),
    ...(lookupKey != null ? { lookupKey } : {}),
    ...(formula != null ? { formula } : {}),
    ...(heightPx != null ? { heightPx } : {}),
    ...(attributes != null ? { attributes } : {}),
    ...(meta != null ? { meta } : {}),
    ...(children != null ? { children: decodeChildSpecList(children) } : {}),
    ...(reconcile != null ? { reconcile } : {}),
  };
}

function decodePosition(value: Option<Json>, index: number): Rational {
  if (typeof value === "number" && Number.isInteger(value) && value >= 0) {
    return Rational.nth(value);
  }
  const obj = Json.object(value);
  const n = Json.number(obj, "n");
  const d = Json.number(obj, "d");
  if (n != null && d != null && d !== 0) return new Rational(n, d);
  return Rational.nth(index);
}

/**
 * Convert a `DataPresetSpec[]` tree (the authoring form used by
 * `data-canvas` variants) into a `ChildSpec[]` tree (the
 * expansion form consumed at hydrate time). Positions are
 * assigned in spec order via `Rational.nth(i)`; child presets
 * inherit their parent's preset key as a prefix (`root/child-0`)
 * so virtual-id derivation stays stable across renders.
 *
 * Ported verbatim from `tento/tento-lona-js/src/row-type.ts:99`.
 */
function dataPresetToChildSpecs(
  specs: readonly DataPresetSpec[],
  parentPreset: Option<string>,
): ChildSpec[] {
  return specs.map((spec, i) => {
    const heightPx = dataPresetHeightPx(spec);
    // D-E preset-name stability (renderer `_type` self-description flip):
    // derived preset names feed `virtual(r_hex|preset)` ids + parent-storage
    // attr keys, so renaming them orphans persisted per-preset state. Renderer
    // marks now author a qualified `_type` (`canvas.points`) instead of the
    // legacy `data-renderer`; derive the preset from the LEGACY-STABLE
    // `"data-renderer"` alias so the id/key names never move.
    const presetType =
      isRendererWireType(spec.type) && spec.type !== "data-renderer"
        ? "data-renderer"
        : spec.type;
    const preset = spec.preset !== undefined
      ? spec.preset
      : parentPreset
        ? `${parentPreset}/${presetType}-${i}`
        : `${presetType}-${i}`;
    return {
      preset,
      type: spec.type,
      ...(typeof spec.label === "string" ? { label: spec.label } : {}),
      ...(typeof spec.attributes?.lookupKey === "string"
        ? { lookupKey: spec.attributes.lookupKey }
        : {}),
      ...(typeof spec.formula === "string" ? { formula: spec.formula } : {}),
      ...(heightPx != null ? { heightPx } : {}),
      position: Rational.nth(i),
      storage: "own" as const,
      placement: "child" as const,
      ...(spec.attributes != null ? { attributes: spec.attributes } : {}),
      ...(spec.graph !== undefined ? { graph: spec.graph } : {}),
      ...(spec.children !== undefined
        ? { children: dataPresetToChildSpecs(spec.children, preset) }
        : {}),
    };
  });
}

function dataPresetHeightPx(spec: DataPresetSpec): Option<number> {
  if (typeof spec.heightPx === "number") return spec.heightPx;
  if (spec.type === "data-canvas") return DATA_GRAPH_ROW_HEIGHTS.canvas;
  if (spec.type === "data-series") return DATA_GRAPH_ROW_HEIGHTS.series;
  return null;
}

/**
 * The built-in per-wire-type metadata: populated at module load
 * (`registerBuiltinRowTypeSpecs`) and frozen there. Package-internal and the
 * source of `SheetRegistries.BUILTINS.rowTypes` only — every read goes through
 * a sheet's `SheetRegistries`; a test layers its fixture specs on the client
 * it builds (`SheetRegistries.extend`), never here.
 */
export const rowTypeSpecRegistry = new StaticRegistry<RowTypeSpec>("ROW_TYPE_SPECS");

/**
 * The built-in per-wire-type metadata, read only — package-internal: the
 * source of `SheetRegistries.BUILTINS.rowTypes`. Lookups fall back to `null`
 * (no spec) for unknown or unregistered types.
 */
export const ROW_TYPE_SPECS = {
  /** Look up a spec by wire type. `null` when unregistered. */
  get(wireType: string): Option<RowTypeSpec> {
    return rowTypeSpecRegistry.get(wireType);
  },
} as const;

/**
 * Merge static type defaults with the instance's wire-attributes.
 * Instance values win at the top level. `children` (visibility
 * map) is deep-merged per-preset — instance presets override
 * default presets. A type's child seeds are not attributes: they
 * come from `RowTypeSpec.childSpecs` (see `childSpecsFor`).
 *
 * Ported from `tento/tento-lona-js/src/row-type.ts:71`. The
 * `reconcileAttributes.deno.test.ts` suite pins the behaviour
 * against the lona-js original.
 */
function reconcileAttributes(
  defaults: Option<JsonObject>,
  instance: Option<JsonObject>,
): JsonObject {
  if (!defaults) return instance ? { ...instance } : {};
  if (!instance) return { ...defaults };

  // Deep merge `children` — instance presets override default presets
  const defaultChildren = Json.child(defaults, "children");
  const instanceChildren = Json.child(instance, "children");
  if (defaultChildren && instanceChildren) {
    return {
      ...defaults,
      ...instance,
      children: { ...defaultChildren, ...instanceChildren },
    };
  }
  return { ...defaults, ...instance };
}

/**
 * Convenience wrapper — look up the spec for `wireType` and
 * reconcile `instance` against its `defaultAttributes`. Returns
 * the instance unchanged when the type has no spec or no
 * defaults.
 */
function reconcileForWireType(
  wireType: string,
  rawRow: RawRowInput,
  instance: Option<JsonObject>,
  registries: RowTypeSpecOps.Registries,
): Option<JsonObject> {
  const spec = registries.rowTypes.get(wireType);
  const defaults = spec?.defaultAttributes?.(rawRow);
  if (!defaults && !instance) return null;
  return reconcileAttributes(defaults ?? null, instance);
}

/**
 * Merge static metadata defaults with instance wire metadata.
 * Instance metadata wins. Unlike attributes, metadata is shallow:
 * keys are server/runtime invariants and should not deep-merge
 * opaque nested payloads.
 */
function reconcileMetadataForWireType(
  wireType: string,
  rawRow: RawRowInput,
  instance: Option<RowMeta>,
  registries: RowTypeSpecOps.Registries,
): Option<RowMeta> {
  const spec = registries.rowTypes.get(wireType);
  const defaults = spec?.defaultMetadata?.(rawRow);
  if (!defaults && !instance) return null;
  return {
    ...(defaults ?? {}),
    ...(instance ?? {}),
  };
}

// ── RowTypeSpec namespace ────────────────────────────────────────

const $resolveChildren = resolveChildren;
const $resolveParentRowId = resolveParentRowId;
const $childSpecsFor = childSpecsFor;
const $childSpecsFromAttributes = childSpecsFromAttributes;
const $dataPresetToChildSpecs = dataPresetToChildSpecs;
const $dataPresetHeightPx = dataPresetHeightPx;
const $reconcileAttributes = reconcileAttributes;
const $reconcileForWireType = reconcileForWireType;
const $reconcileMetadataForWireType = reconcileMetadataForWireType;

/**
 * Operations over row-type specs: resolving a type's children and parent and
 * reconciling a row with its type.
 */
export namespace RowTypeSpecOps {
  /**
   * What the row-type reads need of a sheet's registries: its row-type table
   * (`SheetRegistries.rowTypes`; a `SheetRegistries` is one).
   */
  export interface Registries {
    /** The row-type table: the spec a wire type declares, or `null`. */
    readonly rowTypes: {
      /** The spec of `wireType`, or `null` when no row type declares it. */
      get(wireType: string): Option<RowTypeSpec>;
    };
  }

  export const resolveChildren: (specs: readonly ChildSpec[], attributes: Option<JsonObject>) => ChildSpec[] = $resolveChildren;
  export const resolveParentRowId: (attributes: ChildSpecAttributes, parentRowId: RowNetworkKey) => JsonObject = $resolveParentRowId;
  export const childSpecsFor: (wireType: string, row: RawRowInput, registries: RowTypeSpecOps.Registries) => readonly ChildSpec[] = $childSpecsFor;
  export const childSpecsFromAttributes: (attributes: Option<JsonObject>) => readonly ChildSpec[] = $childSpecsFromAttributes;
  export const dataPresetToChildSpecs: (specs: readonly DataPresetSpec[], parentPreset: Option<string>) => ChildSpec[] = $dataPresetToChildSpecs;
  export const dataPresetHeightPx: (spec: DataPresetSpec) => Option<number> = $dataPresetHeightPx;
  export const reconcileAttributes: (defaults: Option<JsonObject>, instance: Option<JsonObject>) => JsonObject = $reconcileAttributes;
  export const reconcileForWireType: (wireType: string, rawRow: RawRowInput, instance: Option<JsonObject>, registries: RowTypeSpecOps.Registries) => Option<JsonObject> = $reconcileForWireType;
  export const reconcileMetadataForWireType: (wireType: string, rawRow: RawRowInput, instance: Option<RowMeta>, registries: RowTypeSpecOps.Registries) => Option<RowMeta> = $reconcileMetadataForWireType;
}
